/*
 * Pure shaping for the timesheet: Board groups, the week strip, the week
 * grid and the Day view's blocks. No React or IO, so it's unit-tested.
 */
import type { CaseOption, SignalRow, TimeEntry, UnreleasedDay } from "./types";
import { isUnreleased, shiftDate, totalHours, weekdayOf } from "./utils";

export const UTBMS_ACTIVITIES: ReadonlyArray<{ code: string; label: string }> =
  [
    { code: "A101", label: "Plan and prepare for" },
    { code: "A102", label: "Research" },
    { code: "A103", label: "Draft/revise" },
    { code: "A104", label: "Review/analyze" },
    { code: "A105", label: "Communicate (in firm)" },
    { code: "A106", label: "Communicate (with client)" },
    { code: "A107", label: "Communicate (other outside counsel)" },
    { code: "A108", label: "Communicate (other external)" },
    { code: "A109", label: "Appear for/attend" },
    { code: "A110", label: "Manage data/files" },
    { code: "A111", label: "Other" }
  ];

export const utbmsLabel = (code?: string | null): string =>
  UTBMS_ACTIVITIES.find((a) => a.code === code)?.label ?? "";

// ---- case colours --------------------------------------------------------

export type CaseTone = { block: string; text: string; dot: string };

const TONES: CaseTone[] = [
  {
    block: "bg-info-soft",
    text: "text-info",
    dot: "bg-info"
  },
  {
    block: "bg-purple-soft",
    text: "text-purple",
    dot: "bg-purple"
  },
  {
    block: "bg-success-soft",
    text: "text-success",
    dot: "bg-success"
  },
  {
    block: "bg-danger-soft",
    text: "text-danger",
    dot: "bg-danger"
  },
  {
    block: "bg-success-soft",
    text: "text-success",
    dot: "bg-success"
  },
  {
    block: "bg-info-soft",
    text: "text-info",
    dot: "bg-info"
  }
];

export const NEEDS_CASE_TONE: CaseTone = {
  block: "bg-warn-soft",
  text: "text-warn",
  dot: "bg-warn"
};

/** A stable colour per case; amber is kept for "needs a case". */
export const caseTone = (individualId?: string | null): CaseTone => {
  if (!individualId) return NEEDS_CASE_TONE;
  let h = 0;
  for (let i = 0; i < individualId.length; i += 1)
    h = (h * 31 + individualId.charCodeAt(i)) % 2_147_483_647;
  return TONES[h % TONES.length];
};

// ---- the Board -----------------------------------------------------------

export type CarryGroup = { day: UnreleasedDay; entries: TimeEntry[] };

export type BoardGroups = {
  carry: CarryGroup[];
  needsCase: TimeEntry[];
  toReview: TimeEntry[];
  released: TimeEntry[];
};

const byStart = (a: TimeEntry, b: TimeEntry) =>
  (a.started_at ?? a.created_at ?? "").localeCompare(
    b.started_at ?? b.created_at ?? ""
  );

/**
 * The day's columns. Earlier unreleased days (carry-over) show on top only
 * when looking at today; returned entries sit with the ones to review.
 */
export const boardGroups = (
  entries: readonly TimeEntry[],
  date: string,
  today: string,
  carryDays: readonly UnreleasedDay[] = []
): BoardGroups => {
  const live = entries.filter((e) => e.status !== "discarded");
  const todays = live.filter((e) => e.work_date === date).sort(byStart);
  const carry =
    date === today
      ? carryDays
          .filter((d) => d.work_date < today)
          .map((day) => ({
            day,
            entries: live
              .filter(
                (e) => e.work_date === day.work_date && isUnreleased(e.status)
              )
              .sort(
                (a, b) =>
                  Number(Boolean(a.individual_id)) -
                    Number(Boolean(b.individual_id)) || byStart(a, b)
              )
          }))
          .filter((g) => g.entries.length > 0)
          .reverse()
      : [];
  return {
    carry,
    needsCase: todays.filter((e) => e.status === "needs_case"),
    toReview: todays.filter(
      (e) => isUnreleased(e.status) && e.status !== "needs_case"
    ),
    released: todays.filter((e) => !isUnreleased(e.status))
  };
};

type Releasing = Pick<
  TimeEntry,
  "individual_id" | "billable" | "fee_arrangement" | "narrative"
>;

/** Why an entry can't be released yet; the server checks the same. */
export const releaseBlock = (e: Releasing): string | null => {
  if (!e.individual_id) return "Choose a case first";
  const hourly =
    e.fee_arrangement === "hourly" || e.fee_arrangement === "hourly_capped";
  if (e.billable && hourly && !e.narrative.trim())
    return "Hourly time needs a narrative";
  return null;
};

/** What "Release all" sends: everything unreleased on screen that's ready. */
export const releasable = (groups: BoardGroups): TimeEntry[] =>
  [...groups.carry.flatMap((g) => g.entries), ...groups.toReview].filter(
    (e) => !releaseBlock(e)
  );

/** Merge needs two or more entries on one case. */
export const canMerge = (entries: readonly TimeEntry[]): boolean => {
  if (entries.length < 2) return false;
  const cases = new Set(entries.map((e) => e.individual_id));
  return cases.size === 1 && !cases.has(null) && !cases.has(undefined);
};

export type DayStat = {
  date: string;
  captured: number;
  released: number;
  unreleased: number;
};

export const dayStats = (
  entries: readonly TimeEntry[],
  dates: readonly string[]
): Record<string, DayStat> => {
  const out: Record<string, DayStat> = {};
  for (const date of dates) {
    const list = entries.filter(
      (e) => e.work_date === date && e.status !== "discarded"
    );
    out[date] = {
      date,
      captured: totalHours(list),
      released: totalHours(list.filter((e) => !isUnreleased(e.status))),
      unreleased: list.filter((e) => isUnreleased(e.status)).length
    };
  }
  return out;
};

export const weekDates = (monday: string): string[] =>
  Array.from({ length: 7 }, (_, i) => shiftDate(monday, i));

/** The header stats for one day. */
export const daySummary = (entries: readonly TimeEntry[], date: string) => {
  const list = entries.filter(
    (e) => e.work_date === date && e.status !== "discarded"
  );
  return {
    captured: totalHours(list),
    released: totalHours(list.filter((e) => !isUnreleased(e.status))),
    value: Math.round(
      list.reduce(
        (s, e) => s + (e.fee_arrangement === "flat_fee" ? 0 : e.amount || 0),
        0
      )
    )
  };
};

// ---- the Week view -------------------------------------------------------

export type WeekRow = {
  individual_id: string;
  label: string;
  flatFee: boolean;
  hours: number[];
  total: number;
};

/** Hours per case per weekday (Mon–Fri, plus the weekend when it has time). */
export const weekGrid = (
  entries: readonly TimeEntry[],
  dates: readonly string[]
) => {
  const rows = new Map<string, WeekRow>();
  entries.forEach((e) => {
    const col = dates.indexOf(e.work_date);
    if (e.status === "discarded" || !e.individual_id || col === -1) return;
    const row =
      rows.get(e.individual_id) ??
      ({
        individual_id: e.individual_id,
        label: e.case_label,
        flatFee: e.fee_arrangement === "flat_fee",
        hours: dates.map(() => 0),
        total: 0
      } satisfies WeekRow);
    row.hours[col] =
      Math.round((row.hours[col] + e.duration_hours) * 100) / 100;
    row.total = Math.round((row.total + e.duration_hours) * 100) / 100;
    rows.set(e.individual_id, row);
  });
  const list = Array.from(rows.values()).sort(
    (a, b) => b.total - a.total || a.label.localeCompare(b.label)
  );
  const columns = dates.map(
    (_, i) => Math.round(list.reduce((s, r) => s + r.hours[i], 0) * 100) / 100
  );
  const unassigned = totalHours(
    entries.filter(
      (e) =>
        e.status !== "discarded" &&
        !e.individual_id &&
        dates.includes(e.work_date)
    )
  );
  return {
    rows: list,
    columns,
    total: Math.round(columns.reduce((a, b) => a + b, 0) * 100) / 100,
    unassigned
  };
};

// ---- the Day view --------------------------------------------------------

/** Hours since local midnight for an ISO time (fractional). */
export const hourOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
};

export type DayBlock = { entry: TimeEntry; start: number; end: number };

const MIN_BLOCK_H = 0.25;

/** Entries with a time span, as blocks; the rest are listed beside the grid. */
export const dayBlocks = (entries: readonly TimeEntry[]) => {
  const blocks: DayBlock[] = [];
  const untimed: TimeEntry[] = [];
  entries.forEach((entry) => {
    if (entry.status === "discarded") return;
    const start = hourOf(entry.started_at);
    if (start === null) {
      untimed.push(entry);
      return;
    }
    const ended = hourOf(entry.ended_at);
    const end = Math.max(
      ended !== null && ended > start ? ended : start + entry.duration_hours,
      start + MIN_BLOCK_H
    );
    blocks.push({ entry, start, end: Math.min(end, 24) });
  });
  blocks.sort((a, b) => a.start - b.start);
  return { blocks, untimed };
};

/** The hour range the grid shows: 8–18, widened to fit what happened. */
export const gridRange = (
  blocks: readonly DayBlock[],
  signals: readonly SignalRow[] = []
) => {
  const starts = [
    ...blocks.map((b) => b.start),
    ...signals.map((s) => hourOf(s.started_at) ?? 12)
  ];
  const ends = [
    ...blocks.map((b) => b.end),
    ...signals.map((s) => hourOf(s.ended_at) ?? 12)
  ];
  const from = Math.max(0, Math.min(8, ...starts.map(Math.floor)));
  const to = Math.min(24, Math.max(18, ...ends.map(Math.ceil)));
  return { from, to };
};

/** Gaps of half an hour or more between blocks, up to `limit` (now, or the grid's end). */
export const dayGaps = (blocks: readonly DayBlock[], limit: number) => {
  const gaps: { from: number; to: number }[] = [];
  for (let i = 0; i < blocks.length; i += 1) {
    const from = blocks[i].end;
    const to = i + 1 < blocks.length ? blocks[i + 1].start : limit;
    if (to - from >= 0.5) gaps.push({ from, to });
  }
  return gaps;
};

/** Is [a, b) free of blocks? */
export const isFree = (
  blocks: readonly DayBlock[],
  a: number,
  b: number
): boolean => !blocks.some((x) => a < x.end && b > x.start);

/** An ISO timestamp for `hours` past local midnight on `date`. */
export const isoAt = (date: string, hours: number): string => {
  const [y, m, d] = date.split("-").map(Number);
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  return new Date(y, m - 1, d, whole, minutes).toISOString();
};

export const clockLabel = (hours: number): string => {
  const h = Math.floor(hours) % 24;
  const m = Math.round((hours - Math.floor(hours)) * 60);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m
    ? `${h12}:${String(m).padStart(2, "0")} ${suffix}`
    : `${h12} ${suffix}`;
};

export const clockRange = (a: number, b: number): string =>
  `${clockLabel(a)} – ${clockLabel(b)}`;

/** "From Wednesday" for a carry-over day, "From Wednesday, Oct 1" past a week. */
export const carryLabel = (day: string, today: string): string => {
  const weekday = weekdayOf(day);
  if (day >= shiftDate(today, -6)) return `From ${weekday}`;
  const [y, m, d] = day.split("-").map(Number);
  const label = new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
  return `From ${weekday}, ${label}`;
};

/**
 * The picker's second line: the employer, plus the opened date when another
 * result has the same label ("Acme · Opened Mar 4, 2026").
 */
export const caseOptionDetail = (
  option: CaseOption,
  options: CaseOption[]
): string => {
  const twin = options.some(
    (o) => o.individual_id !== option.individual_id && o.label === option.label
  );
  const parts = [option.employer];
  if (twin && option.opened) {
    const [y, m, d] = option.opened.split("-").map(Number);
    parts.push(
      `Opened ${new Date(y, m - 1, d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      })}`
    );
  }
  return parts.filter(Boolean).join(" · ");
};

/** "Wednesday, October 7". */
export const longDate = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric"
  });
};
